import React, { useEffect, useRef, useState } from 'react';
import {
  Card, Button, Space, message, Form, Input, Switch, Upload,
  Typography, InputNumber, Image, Divider, Row, Col, Tag,
} from 'antd';
import {
  ArrowLeftOutlined, UploadOutlined, SaveOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient } from 'Utils/api';
import { useLanguage } from '../../Context/LanguageContext';

const { Title, Text } = Typography;

const apiBase = process.env.REACT_APP_API_URL || '';
const resolveImg = (url: string) => /^https?:\/\//i.test(url) ? url : `${apiBase}${url}`;

type LangMap = Record<string, string>;
interface SlideTranslatableFields {
  title: LangMap;
  subtitle: LangMap;
  ctaLabel: LangMap;
}
interface FormValues {
  imageUrl: string;
  ctaUrl: string;
  sortOrder: number;
  isActive: boolean;
  title: string;
  subtitle: string;
  ctaLabel: string;
}

const DEFAULT_LANG = 'de';
const TRANSLATABLE_FIELDS: (keyof SlideTranslatableFields)[] = ['title', 'subtitle', 'ctaLabel'];

const HeroSlideEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const [form] = Form.useForm<FormValues>();
  const { currentLanguage } = useLanguage();
  const prevLangRef = useRef<string>(currentLanguage);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [langData, setLangData] = useState<SlideTranslatableFields>({
    title: {}, subtitle: {}, ctaLabel: {},
  });

  const previewImage = Form.useWatch('imageUrl', form);

  useEffect(() => {
    if (isNew) {
      const initial: SlideTranslatableFields = {
        title: { de: '', en: '' },
        subtitle: { de: '', en: '' },
        ctaLabel: { de: 'KOLLEKTION ENTDECKEN', en: 'EXPLORE COLLECTION' },
      };
      setLangData(initial);
      form.setFieldsValue({
        isActive: true,
        sortOrder: 1,
        ctaUrl: '/kaufen',
        imageUrl: '',
        title: initial.title[currentLanguage] || '',
        subtitle: initial.subtitle[currentLanguage] || '',
        ctaLabel: initial.ctaLabel[currentLanguage] || '',
      });
    } else {
      fetchSlide();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Sync form values when global language switcher changes
  useEffect(() => {
    const prevLang = prevLangRef.current;
    if (prevLang === currentLanguage) return;

    // Persist values currently in the form for the previous language
    const current = form.getFieldsValue();
    setLangData((prev) => {
      const next: SlideTranslatableFields = {
        title: { ...prev.title, [prevLang]: current.title || '' },
        subtitle: { ...prev.subtitle, [prevLang]: current.subtitle || '' },
        ctaLabel: { ...prev.ctaLabel, [prevLang]: current.ctaLabel || '' },
      };
      // Load values for the new language
      form.setFieldsValue({
        title: next.title[currentLanguage] || '',
        subtitle: next.subtitle[currentLanguage] || '',
        ctaLabel: next.ctaLabel[currentLanguage] || '',
      });
      return next;
    });
    prevLangRef.current = currentLanguage;
  }, [currentLanguage, form]);

  const fetchSlide = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/api/admin/hero-slides/${id}`);
      const slide = res.data.heroSlide;
      if (!slide) {
        message.error('Slide nicht gefunden');
        navigate('/hero-slider');
        return;
      }
      const initial: SlideTranslatableFields = {
        title: slide.title || {},
        subtitle: slide.subtitle || {},
        ctaLabel: slide.ctaLabel || {},
      };
      setLangData(initial);
      form.setFieldsValue({
        imageUrl: slide.imageUrl,
        ctaUrl: slide.ctaUrl || '',
        sortOrder: slide.sortOrder,
        isActive: slide.isActive,
        title: initial.title[currentLanguage] || '',
        subtitle: initial.subtitle[currentLanguage] || '',
        ctaLabel: initial.ctaLabel[currentLanguage] || '',
      });
      prevLangRef.current = currentLanguage;
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Fehler beim Laden');
    } finally { setLoading(false); }
  };

  const handleFormChange = (changed: Partial<FormValues>) => {
    const updates: Partial<SlideTranslatableFields> = {};
    TRANSLATABLE_FIELDS.forEach((f) => {
      if (changed[f] !== undefined) {
        updates[f] = { ...langData[f], [currentLanguage]: (changed[f] as string) || '' };
      }
    });
    if (Object.keys(updates).length) {
      setLangData((prev) => ({ ...prev, ...updates }));
    }
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('images', file);
      const res = await apiClient.post('/api/admin/hero-slides/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.imageUrl) {
        form.setFieldsValue({ imageUrl: res.data.imageUrl });
        message.success('Bild hochgeladen');
      }
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Upload fehlgeschlagen');
    } finally { setUploading(false); }
    return false;
  };

  const submit = async () => {
    try {
      const v: FormValues = await form.validateFields();
      // Capture current language values into langData before send
      const finalLang: SlideTranslatableFields = {
        title: { ...langData.title, [currentLanguage]: v.title || '' },
        subtitle: { ...langData.subtitle, [currentLanguage]: v.subtitle || '' },
        ctaLabel: { ...langData.ctaLabel, [currentLanguage]: v.ctaLabel || '' },
      };
      setSaving(true);
      const payload = {
        imageUrl: v.imageUrl,
        title: finalLang.title,
        subtitle: finalLang.subtitle,
        ctaLabel: finalLang.ctaLabel,
        ctaUrl: v.ctaUrl || null,
        sortOrder: v.sortOrder,
        isActive: v.isActive,
      };
      if (isNew) {
        await apiClient.post('/api/admin/hero-slides', payload);
        message.success('Slide angelegt');
      } else {
        await apiClient.patch(`/api/admin/hero-slides/${id}`, payload);
        message.success('Slide aktualisiert');
      }
      navigate('/hero-slider');
    } catch (e: any) {
      if (!e.errorFields) {
        message.error(e.response?.data?.message || 'Fehler');
      }
    } finally { setSaving(false); }
  };

  if (loading) return <div>Lade Slide…</div>;

  const isDefault = currentLanguage === DEFAULT_LANG;
  const langSuffix = isDefault ? '' : ` (${currentLanguage.toUpperCase()})`;
  const dePlaceholder = (field: keyof SlideTranslatableFields) =>
    isDefault ? '' : (langData[field]?.de || '');
  const translatedFieldsHaveAnyValue = TRANSLATABLE_FIELDS.some(
    (f) => (langData[f]?.[currentLanguage] || '').trim().length > 0,
  );

  return (
    <div>
      <Space style={{ marginBottom: 16, justifyContent: 'space-between', width: '100%' }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hero-slider')}>
            Zurück
          </Button>
          <Title level={3} style={{ margin: 0 }}>
            {isNew ? 'Neuer Slide' : 'Slide bearbeiten'}
          </Title>
        </Space>
        <Tag color={isDefault ? 'blue' : 'gold'} data-testid="active-language-tag">
          Sprache: {currentLanguage.toUpperCase()}{isDefault ? ' (Standard)' : ''}
        </Tag>
      </Space>

      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleFormChange}
      >
        <Row gutter={24}>
          <Col xs={24} lg={14}>
            <Card
              title={`Inhalte${langSuffix}`}
              style={{ marginBottom: 16 }}
              extra={
                !isDefault && !translatedFieldsHaveAnyValue ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Hinweis: deutsche Werte werden als Placeholder genutzt
                  </Text>
                ) : null
              }
            >
              <Form.Item
                label="Titel"
                name="title"
                tooltip={!isDefault ? 'Deutsche Übersetzung wird als Placeholder verwendet' : undefined}
              >
                <Input
                  placeholder={isDefault ? 'z. B. ZEITLOSE ELEGANZ' : (dePlaceholder('title') || 'Deutsche Übersetzung')}
                  data-testid="slide-title"
                />
              </Form.Item>
              <Form.Item
                label="Untertitel"
                name="subtitle"
                tooltip={!isDefault ? 'Deutsche Übersetzung wird als Placeholder verwendet' : undefined}
              >
                <Input
                  placeholder={isDefault
                    ? 'z. B. Entdecken Sie exklusive Luxusuhren'
                    : (dePlaceholder('subtitle') || 'Deutsche Übersetzung')}
                  data-testid="slide-subtitle"
                />
              </Form.Item>
              <Form.Item
                label="CTA-Buttontext"
                name="ctaLabel"
                tooltip={!isDefault ? 'Deutsche Übersetzung wird als Placeholder verwendet' : undefined}
              >
                <Input
                  placeholder={isDefault ? 'KOLLEKTION ENTDECKEN' : (dePlaceholder('ctaLabel') || 'Deutsche Übersetzung')}
                  data-testid="slide-cta-label"
                />
              </Form.Item>
            </Card>

            <Card title="Einstellungen">
              <Row gutter={16}>
                <Col xs={24} md={16}>
                  <Form.Item
                    label="CTA-Ziel-Link"
                    name="ctaUrl"
                    extra="Interner Pfad (z. B. /kaufen) oder externe URL (https://…)"
                  >
                    <Input placeholder="/kaufen" />
                  </Form.Item>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Item label="Reihenfolge" name="sortOrder">
                    <InputNumber min={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Item label="Aktiv" name="isActive" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

          <Col xs={24} lg={10}>
            <Card title="Bild">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => { handleUpload(file as File); return false; }}
                >
                  <Button icon={<UploadOutlined />} loading={uploading}>Bild hochladen</Button>
                </Upload>
                <Form.Item
                  name="imageUrl"
                  rules={[{ required: true, message: 'Bitte Bild hochladen oder URL angeben' }]}
                  style={{ marginBottom: 8 }}
                >
                  <Input placeholder="oder externe URL einfügen (https://...)" />
                </Form.Item>
                {previewImage && (
                  <>
                    <Divider style={{ margin: '8px 0' }} />
                    <Text type="secondary">Vorschau</Text>
                    <Image
                      src={resolveImg(previewImage)}
                      alt="Vorschau"
                      style={{ maxWidth: '100%', borderRadius: 4 }}
                      fallback="/placeholder.svg"
                    />
                  </>
                )}
              </Space>
            </Card>
          </Col>
        </Row>

        <div style={{ marginTop: 24, textAlign: 'right' }}>
          <Space>
            <Button onClick={() => navigate('/hero-slider')}>Abbrechen</Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              onClick={submit}
              data-testid="save-slide-btn"
            >
              Speichern
            </Button>
          </Space>
        </div>
      </Form>
    </div>
  );
};

export default HeroSlideEditorPage;
